import SendInvitationModal from '@/components/actions/send-invitation/send-invitation-modal';
import { toaster } from '@/components/ui/toaster';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useFetchCreator } from '@/hooks/queries/use-fetch-creator';
import { useCompanionPanelStore } from '@/stores/companion-panel-store';
import { FeatureFlag } from '@fluenceur/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Where a card's "invite" and "propose a collaboration" gestures actually open.
 *
 * The wizard is the one the brand already uses from the contact card, not a
 * copy of it: same steps, same programme list, same send. All this host does is
 * put one creator in front of it, on the step the card's workflow names.
 *
 * It lives in the shell rather than in the panel because the panel closes as
 * soon as the wizard opens, and a modal owned by a component that unmounts
 * would close with it.
 *
 * Two things can be missing by the time the brand clicks: the creator the card
 * named, and the collaborations feature. Neither is worth a broken wizard, so
 * the host says so and closes rather than opening a flow that dead-ends.
 */
export const CompanionInvitationCtaHost = () => {
  const { t } = useTranslation();
  const request = useCompanionPanelStore((state) => state.invitationCta);
  const closeInvitationCta = useCompanionPanelStore((state) => state.closeInvitationCta);
  const collabsEnabled = useFeatureFlag(FeatureFlag.COLLABS);

  const {
    data: creator,
    isLoading,
    isError,
  } = useFetchCreator(request?.socialProfileId ?? '', { enabled: Boolean(request) });

  const needsCollabs = request?.mode === 'collab';
  const isBlocked = needsCollabs && !collabsEnabled;

  useEffect(() => {
    if (!request) return;
    if (!isBlocked && !isError) return;

    toaster.create({
      type: 'info',
      description: isBlocked
        ? t('companion.cta.invitation.unavailable')
        : t('companion.cta.invitation.creatorUnavailable'),
    });
    closeInvitationCta();
  }, [request, isBlocked, isError, closeInvitationCta, t]);

  if (!request || isBlocked || isError) return null;
  // The wizard seeds itself once, on open. Opening it before the creator has
  // landed would seed it without one and dead-end the programme step.
  if (isLoading || !creator) return null;

  return (
    <SendInvitationModal
      // A new card gets a new wizard, so nothing a brand typed for one creator
      // survives into another's.
      key={request.actionId}
      isOpen
      onClose={() => closeInvitationCta()}
      creator={creator}
      creatorIdentityId={request.creatorIdentityId}
      initialView={request.mode === 'collab' ? 'typeSelection' : undefined}
      preselectedProgramId={request.programId ?? undefined}
    />
  );
};
